import React, { useCallback, useEffect } from "react";
import * as styles from "./settings-devices.module.scss";
import { Trans, useTranslation } from "react-i18next";
import { VelavuDeviceModel } from "velavu-js-api";
import { useHasPermission } from "../sign-in-context/sign-in-context";
import ComponentSaveBar from "./component-save-bar";
import { VelavuScrollableRoundTable } from "../../elements/velavu-scrollable-round-table";
import * as tableStyles from "../../styles/table.module.scss";
import DeviceIcon from "../../elements/device-icon";
import VelavuInlineSelect from "../../elements/velavu-inline-select";
import Checkbox from "../../elements/checkbox";
import InfoTooltip from "../../elements/info-tooltip";
import VelavuNotice from "../../elements/velavu-notice/velavu-notice";
import { mapDeviceSettingsInterval } from "../../helper/language-helper";
import { WeightedTableColumnLayout } from "../../elements/weighted-table";
import {
	DEFAULT_DEVICE_RATE_SETTINGS,
	DeviceRateSettings,
	useDeviceSettingsState,
} from "./device-settings-state";
import VelavuButton from "../../elements/velavu-button";
import { useVelavuModal } from "../../elements/velavu-modal";
import VelavuAlertModal from "../../elements/velavu-alert-modal/velavu-alert-modal";
import VelavuAlertModalTitle from "../../elements/velavu-alert-modal/velavu-alert-modal-title";
import VelavuAlertModalContent from "../../elements/velavu-alert-modal/velavu-alert-modal-content";
import VelavuAlertModalActions from "../../elements/velavu-alert-modal/velavu-alert-modal-actions";

const STANDARD_INTERVALS: readonly number[] = [
	0, 60, 300, 600, 900, 1800, 3600, 10800, 21600, 43200, 86400,
];
const MOVING_INTERVALS: readonly number[] = [0, 15, 30, 60, 120, 180, 300, 600];
const COOLDOWN_INTERVALS: readonly number[] = [15, 30, 60, 120, 180, 300, 600];

const TABLE_LAYOUT: readonly WeightedTableColumnLayout[] = [
	{ flex: 1 },
	{ width: 108, alignment: "center" },
	{ width: 108, alignment: "center" },
	{ width: 108, alignment: "center" },
	{ width: 108, alignment: "center" },
];

export interface DeviceSettingsV2Props {
	isDirty: boolean;
	setDirty: (dirty: boolean) => void;
}

export default function SettingsDevicesV2(props: DeviceSettingsV2Props) {
	const { t } = useTranslation();
	const { pushModal } = useVelavuModal();

	const canWriteConfig = useHasPermission("rules:write-config");

	//Fetch device models
	const state = useDeviceSettingsState();

	const propsSetDirty = props.setDirty;
	useEffect(() => {
		propsSetDirty(state.isDirty);
	}, [state.isDirty, propsSetDirty]);

	const handleResetToDefaults = useCallback(async () => {
		const confirmed = await pushModal<boolean>((resolve) => (
			<VelavuAlertModal>
				<VelavuAlertModalTitle>
					{t("settings.device_settings.reset_modal_title")}
				</VelavuAlertModalTitle>

				<VelavuAlertModalContent>
					{t("settings.device_settings.reset_modal_body")}
				</VelavuAlertModalContent>

				<VelavuAlertModalActions>
					<VelavuButton
						label={t("common.cancel")}
						variant="outlined"
						fullWidth
						onClick={() => resolve(false)}
					/>

					<VelavuButton
						label={t("common.reset")}
						variant="danger"
						fullWidth
						onClick={() => resolve(true)}
					/>
				</VelavuAlertModalActions>
			</VelavuAlertModal>
		));

		if (confirmed === true) {
			await state.resetToDefaults();
		}
	}, [pushModal, state.resetToDefaults, t]);

	const resultLabels =
		state.lastAction === "reset"
			? {
					ok: t("settings.device_settings.reset_saved"),
					error: t("settings.device_settings.reset_failed"),
				}
			: {
					ok: t("settings.device_settings.saved"),
					error: t("settings.device_settings.save_failed"),
				};

	return (
		<div className={styles.container}>
			<h1 className={styles.title}>
				{t("settings.device_settings.update_frequency")}
			</h1>
			<span className={styles.info}>
				{t("settings.device_settings.define_frequency_info")}
			</span>

			<VelavuScrollableRoundTable
				className={styles.table}
				layout={TABLE_LAYOUT}
			>
				<thead className={tableStyles.scrollableTableHeader}>
					<tr>
						<th>{t("settings.device_settings.table_device")}</th>
						<th>
							<span className={styles.columnHeader}>
								{t("settings.device_settings.table_standard")}
								<InfoTooltip fixed>
									{t(
										"settings.device_settings.tooltip_standard",
									)}
								</InfoTooltip>
							</span>
						</th>
						<th>
							<span className={styles.columnHeader}>
								{t("settings.device_settings.table_dynamic")}
								<InfoTooltip fixed>
									{t(
										"settings.device_settings.tooltip_dynamic",
									)}
								</InfoTooltip>
							</span>
						</th>
						<th>
							<span className={styles.columnHeader}>
								{t("settings.device_settings.table_moving")}
								<InfoTooltip fixed>
									{t(
										"settings.device_settings.tooltip_moving",
									)}
								</InfoTooltip>
							</span>
						</th>
						<th>
							<span className={styles.columnHeader}>
								{t("settings.device_settings.table_cooldown")}
								<InfoTooltip fixed>
									{t(
										"settings.device_settings.tooltip_cooldown",
									)}
								</InfoTooltip>
							</span>
						</th>
					</tr>
				</thead>
				<tbody>
					{(state.entries ?? []).map((entry) => (
						<DeviceModelRow
							key={entry.model.name}
							deviceModel={entry.model}
							settings={entry.settings}
							disabled={!canWriteConfig}
							onSettingsChange={state.updateSettings}
						/>
					))}
				</tbody>
			</VelavuScrollableRoundTable>

			<VelavuNotice type="info">
				<Trans
					i18nKey="settings.device_settings.battery_notice"
					components={{
						emphasis: <span className={styles.noticeEmphasis} />,
					}}
				/>
			</VelavuNotice>

			<ComponentSaveBar
				disabled={
					!props.isDirty ||
					state.isSavingChanges ||
					state.entries === null ||
					!canWriteConfig
				}
				result={state.lastSaveResult}
				labelOK={resultLabels.ok}
				labelError={resultLabels.error}
				onClick={state.saveChanges}
				secondaryAction={
					<VelavuButton
						label={t("settings.device_settings.reset_to_defaults")}
						variant="outlined"
						disabled={
							!state.canResetToDefaults ||
							state.isSavingChanges ||
							state.entries === null ||
							!canWriteConfig
						}
						onClick={handleResetToDefaults}
					/>
				}
			/>
		</div>
	);
}

function DeviceModelRow(props: {
	deviceModel: VelavuDeviceModel;
	settings: DeviceRateSettings;
	disabled: boolean;
	onSettingsChange: (modelName: string, settings: DeviceRateSettings) => void;
}) {
	const { t } = useTranslation();
	const { deviceModel, settings, disabled, onSettingsChange } = props;

	const handleStandardChange = useCallback(
		(standardRate: number) => {
			onSettingsChange(deviceModel.name, { ...settings, standardRate });
		},
		[deviceModel.name, onSettingsChange, settings],
	);

	const handleDynamicToggle = useCallback(() => {
		onSettingsChange(deviceModel.name, {
			...settings,
			dynamic:
				settings.dynamic === null
					? { ...DEFAULT_DEVICE_RATE_SETTINGS.dynamic }
					: null,
		});
	}, [deviceModel.name, onSettingsChange, settings]);

	const handleMovingChange = useCallback(
		(movingRate: number) => {
			if (settings.dynamic === null) {
				return;
			}
			onSettingsChange(deviceModel.name, {
				...settings,
				dynamic: { ...settings.dynamic, movingRate },
			});
		},
		[deviceModel.name, onSettingsChange, settings],
	);

	const handleCooldownChange = useCallback(
		(cooldownRate: number) => {
			if (settings.dynamic === null) {
				return;
			}
			onSettingsChange(deviceModel.name, {
				...settings,
				dynamic: { ...settings.dynamic, cooldownRate },
			});
		},
		[deviceModel.name, onSettingsChange, settings],
	);

	return (
		<tr className={styles.tableRow}>
			<td>
				<div className={styles.deviceCell}>
					<DeviceIcon model={deviceModel.name} size={16} />
					<span>{deviceModel.name}</span>
				</div>
			</td>
			<td>
				<div className={styles.centeredCell}>
					<VelavuInlineSelect
						value={settings.standardRate}
						onChange={handleStandardChange}
						disabled={disabled}
					>
						{STANDARD_INTERVALS.map((seconds) => (
							<option key={seconds} value={seconds}>
								{mapDeviceSettingsInterval(seconds, t)}
							</option>
						))}
					</VelavuInlineSelect>
				</div>
			</td>
			<td>
				<div className={styles.centeredCell}>
					<Checkbox
						checked={settings.dynamic !== null}
						onClick={handleDynamicToggle}
					/>
				</div>
			</td>
			<td>
				<div className={styles.centeredCell}>
					{settings.dynamic !== null ? (
						<VelavuInlineSelect
							value={settings.dynamic.movingRate}
							onChange={handleMovingChange}
							disabled={disabled}
						>
							{MOVING_INTERVALS.map((seconds) => (
								<option key={seconds} value={seconds}>
									{mapDeviceSettingsInterval(seconds, t)}
								</option>
							))}
						</VelavuInlineSelect>
					) : (
						<span className={styles.dash}>–</span>
					)}
				</div>
			</td>
			<td>
				<div className={styles.centeredCell}>
					{settings.dynamic !== null ? (
						<VelavuInlineSelect
							value={settings.dynamic.cooldownRate}
							onChange={handleCooldownChange}
							disabled={disabled}
						>
							{COOLDOWN_INTERVALS.map((seconds) => (
								<option key={seconds} value={seconds}>
									{mapDeviceSettingsInterval(seconds, t)}
								</option>
							))}
						</VelavuInlineSelect>
					) : (
						<span className={styles.dash}>–</span>
					)}
				</div>
			</td>
		</tr>
	);
}
